import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { useAppDependenciesContext } from '../../state';
import { roomAtom } from '../../video-sdk/core/atoms';
import { RemoteParticipant } from '../../video-sdk/core/room/participant/RemoteParticipant';
import { participantSidsAtom } from '../../video-sdk/core/room/participant/atoms';
import { remoteParticipantToDiagnosticInfo } from '../../video-sdk/core/utils';
import { useParticipantConnectedEventHandlerCallback as useBaseParticipantConnectedEventHandlerCallback } from './useParticipantConnectedEventHandlerCallback';

/**
 * Syncs the connected remote participants in the room
 * (source of truth), with the app state. If a remote participant is connected
 * in the room but missing from the app state, then they will be handled as if
 * they just connected (by calling the handler for the `participantConnected`
 * event).
 *
 * This is useful for initializing the app state with the correct set of
 * connected participants when joining a room, as well as for syncing the state
 * in edge cases where we might have missed a `participantConnected` event.
 */
export function useSyncConnectedParticipantsCallback({
  useParticipantConnectedEventHandlerCallback = useBaseParticipantConnectedEventHandlerCallback,
}: {
  /**
   * Dependency injection for testing purposes. Defaults to the base handler:
   * {@link useBaseParticipantConnectedEventHandlerCallback}.
   */
  useParticipantConnectedEventHandlerCallback?: typeof useBaseParticipantConnectedEventHandlerCallback;
} = {}) {
  const handleConnectedParticipant = useParticipantConnectedEventHandlerCallback();
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const getParticipantSids = useAtomCallback(useCallback((get) => get(participantSidsAtom), []));
  const { logger } = useAppDependenciesContext();

  return useCallback(
    () => {
      const room = getRoom();
      if (!room) return [];

      const roomConnectedParticipants = Array.from(room.remoteParticipants.values()).filter((p) => p.isConnected);
      const storedParticipants = new Set(getParticipantSids());

      const missingParticipants: RemoteParticipant[] = [];
      roomConnectedParticipants.forEach((participant) => {
        const isMissingParticipant = !storedParticipants.has(participant.sid);
        if (isMissingParticipant) {
          logger.info('Handling participant missing from stored state', remoteParticipantToDiagnosticInfo(participant));
          missingParticipants.push(participant);
          handleConnectedParticipant(participant);
        }
      });

      return missingParticipants;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
